import { CalculatedMetric } from "@/types/setting"
import { MetricMeta } from "@/types/metric"
import { Unit } from "@/types/setting"
import { SummaryImpl } from "@/app/report/common/constructor"
import { MetricMapType } from "@/app/report/common/type"
import { getSummaryImplValue } from "@/app/report/common/tools"
import { extractMetricKeys } from "@/app/settings/metric/calculator/utils/formulaTokenizer"
import { evaluateFormula } from "@/app/settings/metric/calculator/utils/formulaValidation"

/**
 * 표 행(선수·그룹) Summary 들을 컬럼 방향으로 합쳐 Total / Average 요약값을 만드는 집계 헬퍼 (일감 #98).
 *
 * 규칙은 백엔드 ohcoach-core `dto/report/Summary.java` 의 sum/avg(sumByMultipleData / avgByMultipleData)를
 * 그대로 옮긴 것이다 — Daily Stats 가 서버에서 받는 total/average 행과 같은 값이 나오도록 한다.
 * 화면·PDF·CSV 는 모두 이 함수 하나만 써야 한다 (규칙이 흩어지면 산출물 간 값이 어긋난다).
 *
 * | 규칙          | Total 행            | Average 행          | 대상                                                    |
 * |---------------|---------------------|---------------------|---------------------------------------------------------|
 * | sum           | Σ                   | Σ ÷ n               | 거리·시간·존 거리/시간/진입·액션 count/distance·IMU 횟수·workload·rpe |
 * | max           | max                 | Σ(max) ÷ n          | maxSpeed·maxAcceleration·maxDeceleration·maxHr·액션 Max Power/Distance |
 * | maxKeep       | max                 | max (나누지 않음)   | maxSpeedPercent·maxAccelerationPercent·maxDecelerationPercent·maxHrPercent |
 * | mean          | Σ ÷ n               | Σ ÷ n               | distancePerMins·speedZone*DistancePerMin·avgHr·hrZone*DurationPer·ACR |
 * | weightedPerMin| Σ거리 ÷ Σ시간 × 60  | 동일                | speedZone*DistancePerMin(Rel|Custom) — 서버 PlayerSummaryDto 가중 평균 |
 * | formula       | Total 값에 수식     | Average 값에 수식   | 커스텀 계산 메트릭 — 서버가 요약 Summary 에 수식을 재적용하는 것과 동일 |
 *
 * 분모(n)·제외 규칙도 서버와 같다: general 계열은 totalDistance ≤ 0 행 제외, hr 계열은 avgHr 없는 행 제외,
 * workload 는 0 이하 제외, 그 외는 값이 null 인 행 제외(GK 전용 다이브 지표의 비-GK 행 등).
 */

export type SummaryAggregationRule =
  | "sum"
  | "max"
  | "maxKeep"
  | "mean"
  | "weightedPerMin"
  | "formula"

export type SummaryAggregateResult = {
  total: Record<string, number | null>
  average: Record<string, number | null>
}

const MAX_KEEP_KEYS = new Set([
  "maxSpeedPercent",
  "maxAccelerationPercent",
  "maxDecelerationPercent",
  "maxHrPercent",
])
const MAX_KEYS = new Set(["maxSpeed", "maxAcceleration", "maxDeceleration", "maxHr"])
const MEAN_KEYS = new Set(["distancePerMins", "avgHr"])

const ACTION_MAX_PATTERN = /^max(Run|Accel|Decel)Effort\d+(Power|Distance)(Rel|Custom)?$/
const SPEED_ZONE_PER_MIN_PATTERN = /^speedZone(\d+)DistancePerMin(Rel|Custom)?$/
const HR_ZONE_PERCENT_PATTERN = /^hrZone\d+DurationPer(Rel|Custom)?$/
const ACR_PATTERN = /^acr[A-Z]/
// 키 규칙에 안 걸리는 신규 지표의 안전망 — 비율·강도 단위는 합산하지 않는다
const RATE_UNIT_PATTERN = /(\/min|%|bpm|km\/h|m\/s|mph|yd\/s|ft\/s)/i

export function resolveAggregationRule(
  metricKey: string,
  metric?: MetricMeta,
  unit?: string | null,
): SummaryAggregationRule {
  if (metric?.path?.[0] === "calculatedMetrics") {
    return "formula"
  }
  if (MAX_KEEP_KEYS.has(metricKey)) {
    return "maxKeep"
  }
  if (MAX_KEYS.has(metricKey) || ACTION_MAX_PATTERN.test(metricKey)) {
    return "max"
  }
  const perMin = metricKey.match(SPEED_ZONE_PER_MIN_PATTERN)
  if (perMin) {
    // 일반(general) 존 분당 거리는 서버가 산술평균, Rel/Custom 은 가중 평균
    return perMin[2] ? "weightedPerMin" : "mean"
  }
  if (
    MEAN_KEYS.has(metricKey) ||
    HR_ZONE_PERCENT_PATTERN.test(metricKey) ||
    ACR_PATTERN.test(metricKey)
  ) {
    return "mean"
  }
  if (unit && RATE_UNIT_PATTERN.test(unit) && !/^(m|yd|ft|km|mi)$/i.test(unit)) {
    return "mean"
  }

  return "sum"
}

type Reader = (row: SummaryImpl, key: string) => number | null

function readNumber(source: SummaryImpl, key: string, type: string): number | null {
  const raw = source.get(key, type)
  if (raw === null || raw === undefined) {
    return null
  }
  if (typeof raw === "object") {
    const inner = (raw as unknown as { value?: unknown }).value

    return inner === null || inner === undefined ? null : Number(inner)
  }

  return Number(raw)
}

/** 서버 Summary.java 의 가족(general/hr/…)별 병합 제외 규칙 */
function isRowIncluded(
  row: SummaryImpl,
  metricKey: string,
  metric: MetricMeta | undefined,
): boolean {
  const family = metric?.path?.[0] ?? "general"

  if (family === "general") {
    if (metricKey === "workload") {
      const workload = readNumber(row, "workload", "general")

      return workload !== null && workload > 0
    }
    const totalDistance = readNumber(row, "totalDistance", "general")

    // totalDistance 를 모르는 행(필드 미포함)은 제외하지 않는다
    return totalDistance === null || totalDistance > 0
  }
  if (family === "hr") {
    const avgHr = readNumber(row, "avgHr", "hr")

    return avgHr === null || avgHr > 0
  }

  return true
}

function collect(rows: SummaryImpl[], key: string, metric: MetricMeta | undefined, read: Reader) {
  const values: number[] = []
  rows.forEach((row) => {
    if (!isRowIncluded(row, key, metric)) {
      return
    }
    const value = read(row, key)
    if (value !== null && Number.isFinite(value)) {
      values.push(value)
    }
  })

  return values
}

const sum = (values: number[]) => values.reduce((acc, v) => acc + v, 0)
const mean = (values: number[]) => (values.length ? sum(values) / values.length : null)
const max = (values: number[]) => (values.length ? Math.max(...values) : null)

function aggregateKey(
  rows: SummaryImpl[],
  key: string,
  metric: MetricMeta | undefined,
  rule: SummaryAggregationRule,
  read: Reader,
): { total: number | null; average: number | null } {
  const values = collect(rows, key, metric, read)

  switch (rule) {
    case "sum":
      return { total: values.length ? sum(values) : null, average: mean(values) }
    case "max":
      return { total: max(values), average: mean(values) }
    case "maxKeep":
      return { total: max(values), average: max(values) }
    case "mean":
      return { total: mean(values), average: mean(values) }
    case "weightedPerMin": {
      const matched = key.match(SPEED_ZONE_PER_MIN_PATTERN)
      const suffix = matched?.[2] ?? ""
      const zone = matched?.[1] ?? ""
      let distance = 0
      let duration = 0
      let hasPair = false
      rows.forEach((row) => {
        const d = read(row, `speedZone${zone}Distance${suffix}`)
        const s = read(row, `speedZone${zone}Duration${suffix}`)
        if (d !== null && s !== null && s > 0) {
          distance += d
          duration += s
          hasPair = true
        }
      })
      if (hasPair && duration > 0) {
        const weighted = (distance / duration) * 60

        return { total: weighted, average: weighted }
      }

      return { total: mean(values), average: mean(values) }
    }
    default:
      return { total: null, average: null }
  }
}

export function aggregateSummaryRows({
  rows,
  metricKeys,
  metricMap,
  selectedUnitList,
  calculatedMetrics,
}: {
  rows: SummaryImpl[]
  metricKeys: string[]
  metricMap: MetricMapType
  selectedUnitList?: Unit[]
  calculatedMetrics?: CalculatedMetric[]
}): SummaryAggregateResult {
  const total: Record<string, number | null> = {}
  const average: Record<string, number | null> = {}

  if (!rows.length) {
    metricKeys.forEach((key) => {
      total[key] = null
      average[key] = null
    })

    return { total, average }
  }

  const read: Reader = (row, key) =>
    getSummaryImplValue(key, row, metricMap, selectedUnitList).value

  const formulaOf = (key: string) => calculatedMetrics?.find((m) => m.key === key)?.formula ?? null

  // 1) 수식 메트릭이 참조하는 원재료 키까지 포함해 비-수식 키를 먼저 집계
  const formulaKeys = metricKeys.filter(
    (key) =>
      resolveAggregationRule(key, metricMap.get(key), metricMap.get(key)?.unit) === "formula",
  )
  const baseKeys = new Set(metricKeys.filter((key) => !formulaKeys.includes(key)))
  formulaKeys.forEach((key) => {
    const formula = formulaOf(key)
    if (formula) {
      extractMetricKeys(formula).forEach((ref) => baseKeys.add(ref))
    }
  })

  const baseTotal: Record<string, number | null> = {}
  const baseAverage: Record<string, number | null> = {}
  baseKeys.forEach((key) => {
    const metric = metricMap.get(key)
    const rule = resolveAggregationRule(key, metric, metric?.unit)
    const result = aggregateKey(rows, key, metric, rule, read)
    baseTotal[key] = result.total
    baseAverage[key] = result.average
  })

  // 2) 수식 메트릭은 요약값에 수식을 재적용 (서버 UnitConvertor.convertSummary 와 동일)
  const applyFormula = (formula: string, values: Record<string, number | null>) => {
    const refs = extractMetricKeys(formula)
    const input: Record<string, number> = {}
    for (const ref of refs) {
      const value = values[ref]
      if (value === null || value === undefined) {
        return null
      }
      input[ref] = value
    }

    return evaluateFormula(formula, input)
  }

  metricKeys.forEach((key) => {
    if (formulaKeys.includes(key)) {
      const formula = formulaOf(key)
      total[key] = formula ? applyFormula(formula, baseTotal) : null
      average[key] = formula ? applyFormula(formula, baseAverage) : null

      return
    }
    total[key] = baseTotal[key] ?? null
    average[key] = baseAverage[key] ?? null
  })

  return { total, average }
}
